import { ref, computed, onUnmounted, watch } from 'vue'
import { defineStore, skipHydrate } from 'pinia'

export type ActiveModal = 'login' | 'deposit' | 'withdraw' | 'mobileMenu' | 'mobileSidebar' | 'roundVerification' | 'freeBetBlocked' | 'fpFallback' | null

/**
 * Graphics quality preference.
 *   - 'auto' : the renderer decides based on viewport size, detected GPU
 *              string and real-runtime FPS watchdog verdict (default).
 *   - 'low'  : force the mobile / weak-GPU code paths regardless of device.
 *   - 'high' : force full desktop quality regardless of device — opt-out
 *              for power users on edge hardware that sniffs as weak but
 *              actually runs fine.
 */
export type GraphicsMode = 'auto' | 'low' | 'high'

/** Deposit-modal tab the player last picked; `null` until they pick one. */
export type DepositTab = 'fiat' | 'crypto' | null

// Settings persistence key
const SETTINGS_STORAGE_KEY = 'aurumrun-settings'

interface GameSettings {
  soundEnabled: boolean
  musicEnabled: boolean
  characterAnimationEnabled: boolean
  graphicsMode: GraphicsMode
}

const DEFAULT_SETTINGS: GameSettings = {
  soundEnabled: false,
  musicEnabled: false,
  characterAnimationEnabled: true,
  graphicsMode: 'auto',
}

// Observed prod behavior: the Vercel SSR runtime exposes a TRUTHY but PARTIAL
// `window` (a bundled lib shims it), so `typeof window !== 'undefined'` guards
// PASS during server render and then `window.localStorage` / `window.innerWidth`
// access THROWS ("Failed to read property 'localStorage' from window"). This
// store's setup body runs during SSR of `/`. Feature-detect via try/catch
// instead of presence-detect: actually attempt the read, and on ANY failure
// return the SSR fallback. The fallbacks here are identical to the previous
// `typeof window` fallbacks so the skipHydrate'd SSR payload is unchanged.
function safeRead<T>(fn: () => T, fallback: T): T {
  try {
    return fn()
  } catch {
    return fallback
  }
}

function loadSettings(): GameSettings {
  return safeRead<GameSettings>(() => {
    const stored = localStorage.getItem(SETTINGS_STORAGE_KEY)
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) }
    }
    return DEFAULT_SETTINGS
  }, DEFAULT_SETTINGS)
}

function saveSettings(settings: GameSettings): void {
  safeRead<void>(() => {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings))
  }, undefined)
}

export const useUIStore = defineStore('ui', () => {
  // State - Single source of truth for modal state (mutual exclusivity)
  const activeModal = ref<ActiveModal>(null)

  // `windowWidth` is wrapped in `skipHydrate` for the same reason as
  // `sidebarCollapsed` below: when `/` runs as a real SSR route (see
  // `apps/web/nuxt.config.ts` `routeRules`), the SSR-time setup runs with
  // `typeof window === 'undefined'`, so the SSR payload ships `1024`. The
  // client setup reads the real `window.innerWidth` (e.g. 400 on a phone)
  // — but Pinia's default hydration would then OVERWRITE that real value
  // with the SSR payload's `1024`. `isDesktop` would report `true` on a
  // mobile device until the first resize event fires, and `SidebarOpenFab`
  // would route mobile clicks through the desktop branch of
  // `openRightSidebar()` (toggling the in-flow sidebar instead of opening
  // the overlay). With `skipHydrate`, the client setup value wins from the
  // first paint.
  // SSR fallback `1024` is unchanged; see `safeRead` note above for why a
  // try/catch read (not `typeof window`) is required in this runtime.
  const windowWidth = skipHydrate(ref(safeRead(() => window.innerWidth, 1024)))

  // Game settings - persistent (must init before sidebar to preserve localStorage mock order in tests)
  //
  // skipHydrate: opts these refs out of Pinia's default SSR-state hydration.
  // Without it, the SSR payload (which holds DEFAULT_SETTINGS because
  // `window` is undefined on the server) would overwrite the localStorage
  // values that setup just loaded on the client, and the post-flush watch
  // below would then save the defaults back — wiping out the user's choice.
  // With skipHydrate, the client-side localStorage values win.
  const initialSettings = loadSettings()
  const soundEnabled = skipHydrate(ref(initialSettings.soundEnabled))
  const musicEnabled = skipHydrate(ref(initialSettings.musicEnabled))
  const characterAnimationEnabled = skipHydrate(ref(initialSettings.characterAnimationEnabled))
  const graphicsMode = skipHydrate(ref<GraphicsMode>(initialSettings.graphicsMode))

  // Sidebar collapse - persistent on desktop only.
  // Wrapped in skipHydrate for the same reason as the settings refs above:
  // SSR payload ships `false` (window undefined on the server) and would
  // overwrite the client-loaded localStorage value during hydration,
  // expanding a collapsed sidebar on every reload of a prerendered route.
  const SIDEBAR_STORAGE_KEY = 'aurumrun_sidebar_collapsed'
  // SSR fallback `false` is unchanged; see `safeRead` note above for why a
  // try/catch read (not `typeof window`) is required in this runtime.
  const sidebarCollapsed = skipHydrate(ref(
    safeRead(
      () => window.innerWidth >= 1024
        ? localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'
        : false,
      false
    )
  ))

  watch(sidebarCollapsed, (collapsed) => {
    safeRead<void>(() => {
      if (window.innerWidth >= 1024) {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, String(collapsed))
      }
    }, undefined)
  })

  // Deposit-modal tab (#784). Persisted, and deliberately outside the modal's
  // component tree: the method picker unmounts whenever a method panel opens, so
  // component-local state would reset the tab on every back-navigation. `null`
  // means "never chose one" — the picker then falls back to whichever group its
  // region actually populates, and it does the same when a remembered tab is
  // empty for this region.
  const DEPOSIT_TAB_STORAGE_KEY = 'aurumrun_deposit_tab'
  const depositTab = skipHydrate(ref<DepositTab>(
    safeRead(() => {
      const stored = localStorage.getItem(DEPOSIT_TAB_STORAGE_KEY)
      return stored === 'fiat' || stored === 'crypto' ? stored : null
    }, null),
  ))

  watch(depositTab, (tab) => {
    safeRead<void>(() => {
      if (tab) localStorage.setItem(DEPOSIT_TAB_STORAGE_KEY, tab)
      else localStorage.removeItem(DEPOSIT_TAB_STORAGE_KEY)
    }, undefined)
  })

  // Watch and persist settings changes
  watch(
    [soundEnabled, musicEnabled, characterAnimationEnabled, graphicsMode],
    () => {
      saveSettings({
        soundEnabled: soundEnabled.value,
        musicEnabled: musicEnabled.value,
        characterAnimationEnabled: characterAnimationEnabled.value,
        graphicsMode: graphicsMode.value,
      })
    },
    { flush: 'post' }
  )

  // Mobile sidebar tab state
  const mobileSidebarTab = ref<'players' | 'chat'>('players')

  // Round verification state
  const selectedRoundNumber = ref<number | null>(null)

  // Computed boolean getters for backwards compatibility
  const showLoginModal = computed(() => activeModal.value === 'login')
  const showDepositModal = computed(() => activeModal.value === 'deposit')
  const showWithdrawModal = computed(() => activeModal.value === 'withdraw')
  const showMobileMenu = computed(() => activeModal.value === 'mobileMenu')
  const showMobileSidebar = computed(() => activeModal.value === 'mobileSidebar')
  const showRoundVerificationModal = computed(() => activeModal.value === 'roundVerification')
  const showFreeBetBlockedModal = computed(() => activeModal.value === 'freeBetBlocked')
  const showFpFallbackModal = computed(() => activeModal.value === 'fpFallback')

  // Getters
  const isMobile = computed(() => windowWidth.value < 768)
  const isTablet = computed(() => windowWidth.value >= 768 && windowWidth.value < 1024)
  const isDesktop = computed(() => windowWidth.value >= 1024)

  // Actions
  function closeAllModals(): void {
    activeModal.value = null
  }

  function openLoginModal(): void {
    // Only a real open counts as a funnel step — re-opening while already on the
    // login modal is a no-op and must not double-fire.
    if (activeModal.value !== 'login') useAnalytics().track('login_modal_opened')
    activeModal.value = 'login'
  }

  function closeLoginModal(): void {
    if (activeModal.value === 'login') activeModal.value = null
  }

  function openDepositModal(): void {
    if (activeModal.value !== 'deposit') useAnalytics().track('deposit_modal_opened')
    activeModal.value = 'deposit'
  }

  function closeDepositModal(): void {
    if (activeModal.value === 'deposit') activeModal.value = null
  }

  function openWithdrawModal(): void {
    activeModal.value = 'withdraw'
  }

  function closeWithdrawModal(): void {
    if (activeModal.value === 'withdraw') activeModal.value = null
  }

  function openMobileMenu(): void {
    activeModal.value = 'mobileMenu'
  }

  function closeMobileMenu(): void {
    if (activeModal.value === 'mobileMenu') activeModal.value = null
  }

  function openMobileSidebar(tab?: 'players' | 'chat'): void {
    if (tab) mobileSidebarTab.value = tab
    activeModal.value = 'mobileSidebar'
  }

  function closeMobileSidebar(): void {
    if (activeModal.value === 'mobileSidebar') activeModal.value = null
  }

  function openRoundVerification(roundNumber: number): void {
    selectedRoundNumber.value = roundNumber
    activeModal.value = 'roundVerification'
  }

  function closeRoundVerification(): void {
    if (activeModal.value === 'roundVerification') activeModal.value = null
    selectedRoundNumber.value = null
  }

  function openFreeBetBlockedModal(): void {
    activeModal.value = 'freeBetBlocked'
  }

  function closeFreeBetBlockedModal(): void {
    if (activeModal.value === 'freeBetBlocked') activeModal.value = null
  }

  function openFpFallbackModal(): void {
    activeModal.value = 'fpFallback'
  }

  function closeFpFallbackModal(): void {
    if (activeModal.value === 'fpFallback') activeModal.value = null
  }

  function toggleSidebar(): void {
    sidebarCollapsed.value = !sidebarCollapsed.value
  }

  // Single entry point for "make the right sidebar visible", regardless of
  // viewport. The desktop in-flow sidebar and the <1024 overlay are two
  // separate state machines (`sidebarCollapsed` vs `activeModal`), so the
  // FAB used to branch on `isDesktop` in its click handler — moving that
  // branching here keeps the component dumb and ensures any future caller
  // (a settings menu, a keyboard shortcut) gets the right behavior for free.
  function openRightSidebar(): void {
    if (isDesktop.value) {
      sidebarCollapsed.value = false
    } else {
      openMobileSidebar()
    }
  }

  function updateWindowWidth(width: number): void {
    windowWidth.value = width
  }

  // Initialize window resize listener
  function initResizeListener(): void {
    // Only ever called from a consumer's `onMounted` (client), so it never
    // runs during SSR. Feature-detect `addEventListener` (rather than just
    // presence-detecting `window`) so it stays safe even against the prod
    // partial-window runtime described in the `safeRead` note above.
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return

    const handleResize = (): void => {
      windowWidth.value = safeRead(() => window.innerWidth, windowWidth.value)
    }

    window.addEventListener('resize', handleResize)

    // Cleanup function for composable usage
    onUnmounted(() => {
      window.removeEventListener('resize', handleResize)
    })
  }

  // Settings actions
  function toggleSound(): void {
    soundEnabled.value = !soundEnabled.value
  }

  function toggleMusic(): void {
    musicEnabled.value = !musicEnabled.value
  }

  function toggleCharacterAnimation(): void {
    characterAnimationEnabled.value = !characterAnimationEnabled.value
  }

  function setSoundEnabled(enabled: boolean): void {
    soundEnabled.value = enabled
  }

  function setMusicEnabled(enabled: boolean): void {
    musicEnabled.value = enabled
  }

  function setCharacterAnimationEnabled(enabled: boolean): void {
    characterAnimationEnabled.value = enabled
  }

  function setGraphicsMode(mode: GraphicsMode): void {
    graphicsMode.value = mode
  }

  /**
   * Set to true ONLY by the CrashGraph WebGPU watchdog when recovery has been
   * abandoned (2 consecutive recovery callback failures). The shared
   * `<RecoveryFailedOverlay />` watches this and surfaces a user-actionable
   * Refresh button; `CrashGraphContainer` v-if's the canvas out beneath it.
   * Once true, only a page reload clears it.
   *
   * IMPORTANT: this flag is intentionally SCOPED to crashGraph. Decorative
   * WebGPU sites (cashout runners, character/pet viewers, ore refine modal)
   * MUST NOT set it — their failure should not unmount the main game canvas.
   * See: layers/game/app/composables/useWebGPUWatchdog.ts and
   * memory/project_black_screen_root_cause_two_layer.md for the cascade that
   * motivated this scoping.
   */
  const crashGraphRecoveryFailed = ref(false)

  function setCrashGraphRecoveryFailed(value: boolean): void {
    crashGraphRecoveryFailed.value = value
  }

  /**
   * Set to true by the WebGPU watchdog at the start of `onUnrecoverable`,
   * back to false after `initPixi()` completes. Other PIXI consumers
   * (spectator runners, multiplier animation, child viewers) can watch
   * this flag and skip their per-frame work to avoid touching destroyed
   * stage objects during the destroy → re-init window.
   * See: layers/game/app/composables/useWebGPUWatchdog.ts
   */
  const recovering = ref(false)

  function setRecovering(value: boolean): void {
    recovering.value = value
  }

  return {
    // State
    activeModal,
    showLoginModal,
    showDepositModal,
    showWithdrawModal,
    showMobileMenu,
    showMobileSidebar,
    showRoundVerificationModal,
    showFreeBetBlockedModal,
    showFpFallbackModal,
    selectedRoundNumber,
    mobileSidebarTab,
    sidebarCollapsed,
    windowWidth,
    depositTab,
    // Settings state
    soundEnabled,
    musicEnabled,
    characterAnimationEnabled,
    graphicsMode,
    // Getters
    isMobile,
    isTablet,
    isDesktop,
    // Actions
    closeAllModals,
    openLoginModal,
    closeLoginModal,
    openDepositModal,
    closeDepositModal,
    openWithdrawModal,
    closeWithdrawModal,
    openMobileMenu,
    closeMobileMenu,
    openMobileSidebar,
    closeMobileSidebar,
    openRoundVerification,
    closeRoundVerification,
    openFreeBetBlockedModal,
    closeFreeBetBlockedModal,
    openFpFallbackModal,
    closeFpFallbackModal,
    toggleSidebar,
    openRightSidebar,
    updateWindowWidth,
    initResizeListener,
    // Settings actions
    toggleSound,
    toggleMusic,
    toggleCharacterAnimation,
    setSoundEnabled,
    setMusicEnabled,
    setCharacterAnimationEnabled,
    setGraphicsMode,
    // WebGPU watchdog state
    crashGraphRecoveryFailed,
    setCrashGraphRecoveryFailed,
    recovering,
    setRecovering,
  }
})
